import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { AuthenticatedPage } from '../components/AuthenticatedPage';
import Container from '../components/container/Container';
import { CandidateMarketingToolkit } from '../components/EmploymentReadiness/CandidateMarketingToolkit';
import { EmploymentReadinessHeader } from '../components/EmploymentReadiness/EmploymentReadinessHeader';
import { JobInterview } from '../components/EmploymentReadiness/JobInterview';
import { ProfessionalProfile } from '../components/EmploymentReadiness/ProfessionalProfile';
import { ProfessionalEmail } from '../components/EmploymentReadiness/ProfessionEmailAccount';
import { ReadinessAssessment } from '../components/EmploymentReadiness/ReadinessAssessment';
import { SampleCover } from '../components/EmploymentReadiness/SampleCover';
import { Welcome } from '../components/EmploymentReadiness/Welcome';
import { WorkAttitude } from '../components/EmploymentReadiness/WorkAttitude';
import { usePreEmploymentAssessmentContext } from '../hooks/use-pre-employment-assessment.hooks';
import { useSWOTContext } from '../hooks/use-swot-context.hooks';
import { dispatchGetEmploymentReadinessValues } from '../redux/slice/form-employment-readiness';
import {
  PreEmploymentAssessmentStatus,
  dispatchUpsertPreEmploymentAssessment,
} from '../redux/slice/pre-employment-assessment';
import { dispatchGetSWOTReportValues } from '../redux/slice/swot/swot.operations';
import { SWOTReport } from '../redux/slice/swot/swot.types';
import { useAppDispatch } from '../redux/store';

export const EmploymentReadiness: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const swot: SWOTReport | undefined = useSWOTContext();
  const { componentName } = useParams();
  const { status: preEmploymentAssessmentStatus } = usePreEmploymentAssessmentContext();

  useEffect(() => {
    void dispatch(dispatchGetEmploymentReadinessValues());
    void dispatch(dispatchUpsertPreEmploymentAssessment());
    void dispatch(dispatchGetSWOTReportValues());
  }, []);

  const [step, setStep] = useState(1);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBackButton = () => {
    navigate(`/pre-employment/employment-readiness/step${step - 1}`);
  };

  useEffect(() => {
    setStep(parseInt(componentName?.replace('step', '') || '1'));
    window.scrollTo(0, 0);
  }, [componentName]);

  useEffect(() => {
    if (preEmploymentAssessmentStatus === PreEmploymentAssessmentStatus.NOT_STARTED) {
      void dispatch(
        dispatchUpsertPreEmploymentAssessment({
          status: PreEmploymentAssessmentStatus.ACTIVE,
        }),
      );
    }
  }, [preEmploymentAssessmentStatus]);

  useEffect(() => {
    if (swot) {
      navigate('/swot');
    }
  }, [swot, navigate]);

  const renderFormStep = () => {
    switch (componentName) {
      case 'step1':
        return <Welcome step={step} />;
      case 'step2':
        return <ReadinessAssessment step={step} onBack={handleBackButton} />;
      case 'step3':
        return <CandidateMarketingToolkit step={step} onBack={handleBackButton} />;
      case 'step4':
        return <SampleCover step={step} onBack={handleBackButton} />;
      case 'step5':
        return <ProfessionalProfile step={step} onBack={handleBackButton} />;
      case 'step6':
        return <ProfessionalEmail step={step} onBack={handleBackButton} />;
      case 'step7':
        return <JobInterview step={step} onBack={handleBackButton} />;
      case 'step8':
        return <WorkAttitude step={step} onBack={handleBackButton} />;
      default:
        navigate('/pre-employment/employment-readiness/step1');
        return null;
    }
  };

  return (
    <div className={`steps overflow-hidden relative employment-${step}`}>
      <Container>
        <>
          {step === 1 ? (
            <>{renderFormStep()}</>
          ) : (
            <EmploymentReadinessHeader step={step}>
              <>{renderFormStep()}</>
            </EmploymentReadinessHeader>
          )}
        </>
      </Container>
    </div>
  );
};

export const EmploymentReadinessPage: React.FC = () => {
  return <AuthenticatedPage render={() => <EmploymentReadiness />} />;
};
